import * as $ from "jquery";
import * as _ from "lodash";
import * as Backbone from "backbone";
import { DashboardCategoryCollection } from "../../collections/DashboardCategories";
import { DashboardChannelCollection } from "../../collections/DashboardChannels";
import { DashboardWidgetCollection } from "../../collections/DashboardWidgets";
import { DashboardTabCollection } from "../../collections/DashboardTabs";
import { ModuleCollection } from "../../collections/Modules";
import { TaskQueue } from "../../common/taskQueue";
import { Utils } from "../../common/utils";
import { BaseInternalView } from "../_BaseViews";
import { BulkGenerateModalView } from "../modals/dashboard/BulkGenerateModal";
import { ModifyDashboardModal } from "../modals/dashboard/ModifyDashboardModal";
import { ModifyDashboardNotificationsModal } from "../modals/dashboard/ModifyNotificationsModal";
import { ModifyDashboardWidgetModal } from "../modals/dashboard/modify_widgets/ModifyDashboardWidgetModal";
import { ModifyDashboardTabModal } from "../modals/dashboard/ModifyDashboardTabModal";
import { GenericDeleteModal } from "../modals/GenericDeleteModal";
import { GenericDashboardSelectModal } from "../modals/GenericDashboardSelectModal";
import { DashboardCollection } from "../../collections/Dashboards";
import { DashboardCategoryView } from "./DashboardCategoryView";
import { DashboardTabsView } from "./DashboardTabsView";
import { DashboardWidgetManager } from "./widgets/DashboardWidgetManager";
import { DashboardTab } from "../../models/DashboardTab";

var DASHBOARD_UPDATE_INTERVAL = 30000;
var DASHBOARD_FAILURE_UPDATE_INTERVAL = 3 * DASHBOARD_UPDATE_INTERVAL;
var CHART_UPDATE_MIN_INTERVAL = 5000;

declare var App;


export class DashboardView extends BaseInternalView {

    private template = _.template(require('templates/dashboard/dashboard.html'));
    private dashboardCategoryCollection: DashboardCategoryCollection = null;
    private dashboardWidgetCollection: DashboardWidgetCollection = null;
    private dashboardChannelCollection: DashboardChannelCollection = null;
    private allChannelsCollection: DashboardChannelCollection = null; // Unfiltered collection for Graph category
    private graphedChannelsCollection: DashboardChannelCollection = null; // Only the channels currently graphed in the default chart
    private dashboardTabCollection: DashboardTabCollection = null;
    private moduleCollections: { [key: number]: ModuleCollection };
    private categoryViews: DashboardCategoryView[] = [];
    private graphCategoryView: DashboardCategoryView = null;
    private tabsView: DashboardTabsView = null;
    private widgetManager: DashboardWidgetManager = null;
    private graphWidgetManager: DashboardWidgetManager = null; // Separate widget manager for Graph category

    private dashboardId: number = null;
    private activeTabId: number = null;
    private dashboardUpdateTimer: any = null;
    private chartUpdateTimer: any = null;
    private collectionLoaded = true;
    private dashboardEmpty = true;
    private isLoadingTabData = false; // Flag to prevent duplicate loads during tab switching
    private validWidgets = [1, 3, 4, 5, 6, 7, 8, 14, 15, 16, 17, 21];

    // A hash of the current dashboard configuration (e.g. widgets, categories, channels etc.)
    private dashboardConfigurationHash: number;
    private dashboardConfigurationHashMismatch: number;
    private positionsJustSaved = false;

    constructor(options) {
        super(<any>_.extend({
            tagName: 'div',
            events: {
                'input .dashboard__toolbar .filter': 'handleFilterInput',
                'click .dashboard__toolbar .add-widget': 'handleAddWidgetClick',
                'click .bulk-generate': 'handleBulkGenerateClick',
                'click .edit-dashboard ': 'handleEditDashboardClick',
                'click .clone-dashboard ': 'handleCloneDashboardClick',
                'click .edit-dashboard-notifications ': 'handleEditDashboardNotificationsClick',
            }
        }, options));

        this.resetView();
        this.listenTo(App.Bus, 'dashboard:setLoading', () => {
            this.setLoading();
        });

        this.listenTo(App.Bus, 'dashboard:config:changed', () => {
            // Don't trigger full reload if we're currently loading tab data (prevents duplicate loads)
            if (this.isLoadingTabData) {
                return;
            }

            $.when(this.dashboardCategoryCollection.getCollectionFetchPromise())
                .done(() => {
                    this.setDashboardConfigurationHash();
                    // Re-render categories since the collection may have new/removed categories
                    // (e.g. first widget added creates "All widgets" category)
                    if (!this.dashboardEmpty && this.collectionLoaded) {
                        this.renderSubViews();
                    }
                });

            // Reset the global dashboard channel collection
            $.when(this.options.dashboardChannelCollection.getCollectionFetchPromise())
                .done(() => {
                    this.setNavPriorityIds();
                });

            // Refetch all-channels collection so the default chart can find newly added channels
            if (this.allChannelsCollection) {
                this.allChannelsCollection.getCollectionFetchPromise();
            }

            if (this.dashboardWidgetCollection) {
                var empty = this.isEmpty();

                if (empty !== this.dashboardEmpty) {
                    this.dashboardEmpty = empty;
                    this.show(this.dashboardId);
                }
            }
        });

        this.listenTo(App.Bus, 'dashboard:positions:saved', () => {
            this.positionsJustSaved = true;
        });

        this.listenTo(App.Bus, 'dashboard:category:all:chart:addChannel', () => {
            _.defer(() => this.updateGraphedChannelIds());
        });
        this.listenTo(App.Bus, 'dashboard:category:all:chart:removeChannel', () => {
            _.defer(() => this.updateGraphedChannelIds());
        });
        this.listenTo(App.Bus, 'dashboard:clearAllSelections', () => {
            _.defer(() => this.updateGraphedChannelIds());
        });

        this.listenTo(App.Bus, 'dashboard:update:suspend', () => {
            clearTimeout(this.dashboardUpdateTimer);
            clearTimeout(this.chartUpdateTimer);
        });
        this.listenTo(App.Bus, 'dashboard:update:resume', () => {
            this.dashboardUpdateTimer = setTimeout(_.bind(this.perpetuallyUpdate, this), 5000);
            this.scheduleChartUpdate();
        });
        this.handleFilterInput = _.debounce(this.handleFilterInput, 200);
    }

    private resetView() {
        this.stopListening(this.dashboardWidgetCollection, 'sync');
        if (this.dashboardUpdateTimer) clearTimeout(this.dashboardUpdateTimer);
        this.dashboardUpdateTimer = null;
        if (this.chartUpdateTimer) clearTimeout(this.chartUpdateTimer);
        this.chartUpdateTimer = null;

        this.dashboardConfigurationHash = null;
        this.dashboardChannelCollection = null;
        this.allChannelsCollection = null;
        this.graphedChannelsCollection = null;
        this.dashboardTabCollection = null;
        this.widgetManager = null;
        this.graphWidgetManager = null;

        this.moduleCollections = {};
        this.collectionLoaded = false;

        if (this.tabsView) {
            this.tabsView.remove();
            this.tabsView = null;
        }

        if (this.graphCategoryView) {
            this.graphCategoryView.remove();
            this.graphCategoryView = null;
        }

        _.each(this.categoryViews, function (view) {
            view.remove();
        });
        this.categoryViews = [];
    }

    private handleFilterInput(e) {
        var filterVal = (<string>this.$('.dashboard__toolbar .filter').val());
        filterVal = filterVal === '' ? null : filterVal.toLowerCase();
        App.Bus.trigger('dashboard:filter:notify', filterVal);
    }

    private setNavPriorityIds() {
        // Save dashbaords and installs to storage
        const currentDashboard = this.options.dashboardCollection.findWhere({ id: this.dashboardId });
        const currentDashboardOrg = this.options.organizationCollection.findWhere({
            id: currentDashboard.get("organization_id"),
        });
        const priorityDashboards = [
            {
                name: currentDashboard.get("name"),
                id: currentDashboard.get("id"),
                organizationName: currentDashboardOrg.get("name"),
                organizationId: currentDashboardOrg.get("id"),
            },
        ];
        window.dispatchEvent(new CustomEvent("priority:dashboards", { detail: priorityDashboards }));

        const seen = new Set();
        const priorityInstalls = [];
        this.dashboardChannelCollection.forEach((c) => {
            const installID = c.get("install_id");
            if (!seen.has(installID)) {
                seen.add(installID);
                const install = this.options.installCollection.findWhere({ id: installID });
                if (install) {
                    priorityInstalls.push({
                        id: install.get("id"),
                        name: install.get("organization_install_name"),
                        organizationName: install.get("organization_name"),
                        organizationId: install.get("organization_id"),
                    });
                }
            }
        });
        window.dispatchEvent(new CustomEvent("priority:installs", { detail: priorityInstalls }));
    }

    private setLoading() {
        this.resetView();
        
        // Reset loading flag when starting a fresh load
        this.isLoadingTabData = false;

        this.dashboardChannelCollection = new DashboardChannelCollection([], { dashboardId: this.dashboardId });
        this.dashboardCategoryCollection = new DashboardCategoryCollection([], { dashboardId: this.dashboardId });
        this.dashboardWidgetCollection = new DashboardWidgetCollection([], { dashboardId: this.dashboardId });
        this.dashboardTabCollection = new DashboardTabCollection([], { dashboardId: this.dashboardId });

        this.widgetManager = new DashboardWidgetManager({
            appRegistry: this.options.appRegistry,
            appCollection: this.options.appCollection,
            brandCollection: this.options.brandCollection,
            moduleCollections: this.moduleCollections,
            installCollection: this.options.installCollection,
            permissionCollection: this.options.permissionCollection,
            organizationCollection: this.options.organizationCollection,
            dashboardCollection: this.options.dashboardCollection,
            dashboardChannelCollection: this.dashboardChannelCollection,
            dashboardCategoryCollection: this.dashboardCategoryCollection,
            dashboardWidgetCollection: this.dashboardWidgetCollection,
            dashboardId: this.dashboardId,
            currentUser: this.options.currentUser,
            report: null, // This view is for dashboards, not reports
            validWidgets: this.validWidgets,
        });

        this.render();
    }

    show(dashboardId) {
        // Prevent duplicate loads if already loading
        if (this.isLoadingTabData) {
            return;
        }
        
        this.dashboardId = dashboardId;

        // Calling setLoading will reset the view
        this.setLoading();

        $.when(
            this.dashboardTabCollection.getCollectionFetchPromise()
        ).done(() => {
            // Try to get tabId from URL query parameter
            var urlTabId = this.getTabIdFromUrl();
            
            // Set active tab: use URL param if valid, otherwise default to first tab
            if (this.dashboardTabCollection.length > 0) {
                if (urlTabId !== null) {
                    // Validate that the tab from URL exists in the collection
                    var tabFromUrl = this.dashboardTabCollection.findWhere({ id: urlTabId });
                    if (tabFromUrl) {
                        this.activeTabId = urlTabId;
                    } else {
                        // Tab from URL doesn't exist, default to first tab
                        this.activeTabId = this.dashboardTabCollection.at(0).get('id');
                    }
                } else {
                    // No tab in URL, default to first tab
                    this.activeTabId = this.dashboardTabCollection.at(0).get('id');
                }
            } else {
                this.activeTabId = null;
            }
            
            // Load categories and widgets for the active tab (or all if no tabs)
            this.loadTabData();
        });
    }
    
    private getTabIdFromUrl(): number | null {
        // Parse query string from current URL
        var queryString = window.location.search;
        if (!queryString) {
            // Try to get from hash if using hash-based routing
            var hash = window.location.hash;
            if (hash && hash.indexOf('?') !== -1) {
                queryString = '?' + hash.split('?')[1];
            } else {
                return null;
            }
        }
        
        // Parse query parameters
        var params = new URLSearchParams(queryString);
        var tabParam = params.get('tab');
        
        if (tabParam) {
            var tabId = Number(tabParam);
            if (!isNaN(tabId) && tabId > 0) {
                return tabId;
            }
        }
        
        return null;
    }
    
    private loadTabData() {
        // Prevent duplicate loads if already loading
        if (this.isLoadingTabData) {
            return;
        }
        
        this.isLoadingTabData = true;
        const requestedTabId = this.activeTabId;

        // Set collectionLoaded to false while loading new data
        // Also set dashboardEmpty to false to prevent showing empty message during loading
        this.collectionLoaded = false;
        this.dashboardEmpty = false;
        
        // Clear only tab-specific category views (Graph category stays in separate container)
        _.each(this.categoryViews, function (view) {
            view.remove();
        });
        this.categoryViews = [];
        
        // Clear only tab-specific categories container (Graph category has its own container)
        var categoriesContainer = this.$('.body .categories');
        categoriesContainer.empty();
        
        // Update collections with tabId if we have an active tab
        if (this.activeTabId) {
            this.dashboardCategoryCollection = new DashboardCategoryCollection([], { 
                dashboardId: this.dashboardId,
                tabId: this.activeTabId
            });
            this.dashboardWidgetCollection = new DashboardWidgetCollection([], { 
                dashboardId: this.dashboardId,
                tabId: this.activeTabId
            });
            this.dashboardChannelCollection = new DashboardChannelCollection([], { 
                dashboardId: this.dashboardId,
                tabId: this.activeTabId
            });
        } else {
            // No tabs, load all categories/widgets/channels (backward compatibility)
            this.dashboardCategoryCollection = new DashboardCategoryCollection([], { 
                dashboardId: this.dashboardId
            });
            this.dashboardWidgetCollection = new DashboardWidgetCollection([], { 
                dashboardId: this.dashboardId
            });
            this.dashboardChannelCollection = new DashboardChannelCollection([], { 
                dashboardId: this.dashboardId
            });
        }
        
        // Update widget manager with new collections
        this.widgetManager = new DashboardWidgetManager({
            appRegistry: this.options.appRegistry,
            appCollection: this.options.appCollection,
            brandCollection: this.options.brandCollection,
            moduleCollections: this.moduleCollections,
            installCollection: this.options.installCollection,
            permissionCollection: this.options.permissionCollection,
            organizationCollection: this.options.organizationCollection,
            dashboardCollection: this.options.dashboardCollection,
            dashboardChannelCollection: this.dashboardChannelCollection,
            dashboardCategoryCollection: this.dashboardCategoryCollection,
            dashboardWidgetCollection: this.dashboardWidgetCollection,
            dashboardId: this.dashboardId,
            currentUser: this.options.currentUser,
            report: null,
            validWidgets: this.validWidgets,
        });
        
        // Create separate widget manager for Graph category that uses allChannelsCollection
        // This ensures Graph category widgets have access to all channels across all tabs
        if (!this.allChannelsCollection) {
            this.allChannelsCollection = new DashboardChannelCollection([], {
                dashboardId: this.dashboardId
            });
        }
        if (!this.graphedChannelsCollection) {
            this.graphedChannelsCollection = new DashboardChannelCollection([], {
                dashboardId: this.dashboardId,
                channelIds: []
            });
        }
        this.graphWidgetManager = new DashboardWidgetManager({
            appRegistry: this.options.appRegistry,
            appCollection: this.options.appCollection,
            brandCollection: this.options.brandCollection,
            moduleCollections: this.moduleCollections,
            installCollection: this.options.installCollection,
            permissionCollection: this.options.permissionCollection,
            organizationCollection: this.options.organizationCollection,
            dashboardCollection: this.options.dashboardCollection,
            dashboardChannelCollection: this.allChannelsCollection, // Use unfiltered collection
            dashboardCategoryCollection: this.dashboardCategoryCollection,
            dashboardWidgetCollection: this.dashboardWidgetCollection,
            dashboardId: this.dashboardId,
            currentUser: this.options.currentUser,
            report: null,
            validWidgets: this.validWidgets,
        });

        // Ensure title section is visible - render full template only when structure is missing (initial load)
        // Skip full render on tab switch to preserve tabs DOM and scroll position
        var graphCategoryEl = null;
        if (this.graphCategoryView && this.graphCategoryView.$el.parent().length > 0) {
            graphCategoryEl = this.graphCategoryView.$el.detach();
        }
        
        var needsFullRender = !this.collectionLoaded && this.$('.body .categories').length === 0;
        if (needsFullRender) {
            this.collectionLoaded = true;
            this.render();
            this.collectionLoaded = false;
            
            if (graphCategoryEl) {
                var graphCategoryContainer = this.$('.body .graph-category');
                if (graphCategoryContainer.length > 0) {
                    graphCategoryContainer.append(graphCategoryEl);
                }
            }
        }
        
        // Show loading spinner - ensure categories area is visible and empty state hidden
        this.showLoadingSpinner();

        // Fetch all channels collection for Graph category (needs all channels, not filtered by tab)
        if (!this.allChannelsCollection) {
            this.allChannelsCollection = new DashboardChannelCollection([], { 
                dashboardId: this.dashboardId
            });
        }
        var allChannelsPromise = this.allChannelsCollection.getCollectionFetchPromise();
        
        $.when(
            this.dashboardCategoryCollection.getCollectionFetchPromise(),
            this.dashboardChannelCollection.getCollectionFetchPromise(),
            this.dashboardWidgetCollection.getCollectionFetchPromise(),
            allChannelsPromise
        ).done(() => {
            if (requestedTabId !== this.activeTabId) {
                this.isLoadingTabData = false;
                this.loadTabData();
                return;
            }
            this.collectionLoaded = true;
            this.setDashboardConfigurationHash();
            this.dashboardEmpty = this.isEmpty();

            // Only re-render the content area, not the entire dashboard
            this.renderContentOnly();

            this.listenTo(this.dashboardWidgetCollection, 'sync', () => {
                var newIsEmpty = this.isEmpty();
                if (this.dashboardEmpty !== newIsEmpty) {
                    this.dashboardEmpty = newIsEmpty;
                    this.render()
                }
            });

            this.dashboardUpdateTimer = setTimeout(_.bind(this.perpetuallyUpdate, this), DASHBOARD_UPDATE_INTERVAL);
            this.scheduleChartUpdate();
            this.setNavPriorityIds();
            
            // Clear loading flag after data is loaded
            this.isLoadingTabData = false;
        }).fail(() => {
            this.isLoadingTabData = false;
            if (requestedTabId !== this.activeTabId) {
                this.loadTabData();
            }
        });
    }
    
    private handleTabSwitch(tabId: number) {
        this.activeTabId = tabId;
        this.updateUrlWithTab(tabId);
        if (this.tabsView) this.tabsView.setActiveTab(tabId);
        this.loadTabData();
    }
    
    private updateUrlWithTab(tabId: number) {
        // Update URL with tab query parameter without triggering navigation
        var currentHash = Backbone.history.getHash();
        var baseUrl = '#/dashboard/' + this.dashboardId;
        
        // Remove existing tab parameter if present
        var hashParts = currentHash.split('?');
        var path = hashParts[0];
        var queryString = hashParts[1] || '';
        
        // Update path to match current dashboard
        if (path !== '/dashboard/' + this.dashboardId) {
            path = '/dashboard/' + this.dashboardId;
        }
        
        // Update query parameters
        var params = new URLSearchParams(queryString);
        params.set('tab', String(tabId));
        
        var newUrl = '#' + path + '?' + params.toString();
        Backbone.history.navigate(newUrl, { replace: true });
    }

    private calculateDashboardConfigurationHash() {
        var values = [].concat(
            _.map(this.dashboardChannelCollection.where({
                dashboard_channel_dashboard_id: this.dashboardId
            }), function (c) {
                return c.get('dashboard_name') + '-' +
                    c.get('dashboard_channel_id') + '-' +
                    c.get('dashboard_category_id') + '-' +
                    c.get('dashboard_channel_order') + '-' +
                    c.get('dashboard_widget_type');
            }), this.dashboardCategoryCollection.getETag(), this.dashboardWidgetCollection.getETag()
        ).sort();
        var hash = Utils.String.hash(values.join());
        return hash;
    }

    private setDashboardConfigurationHash() {
        this.dashboardConfigurationHash = this.calculateDashboardConfigurationHash();
        this.dashboardConfigurationHashMismatch = 0;
    }

    private handlePotentialConfigurationHashChange() {
        // Check if widgets have been added, removed, categories changes, or the dashboard renamed
        var currentHash = this.calculateDashboardConfigurationHash();

        if (this.dashboardConfigurationHash !== currentHash) {
            if (this.positionsJustSaved) {
                this.positionsJustSaved = false;
                this.dashboardConfigurationHash = currentHash;
                this.dashboardConfigurationHashMismatch = 0;
                return;
            }
            this.dashboardConfigurationHashMismatch++;
        } else {
            this.dashboardConfigurationHashMismatch = 0;
        }

        if (this.dashboardConfigurationHashMismatch > 2) {
            $.when(this.options.dashboardCollection.getCollectionFetchPromise()).done(() => {
                this.show(this.dashboardId);
            })
        }
    }

    private perpetuallyUpdate() {
        // In case there are somehow multiple timers running, clear one
        clearTimeout(this.dashboardUpdateTimer);

        // Check that we appear to have finished processing the previous update
        // before starting to render the next one.
        if (TaskQueue.shared.isProcessing) {
            // We are still rendering the previous update, so skip this update
            // and schedule the next one.
            console.warn("Skipping dashboard update because previous update is still rendering.");
            setTimeout(_.bind(this.perpetuallyUpdate, this), DASHBOARD_UPDATE_INTERVAL);
            return;
        }

        if (this.$('.body .categories').is(":visible")) {
            var d1 = this.dashboardChannelCollection.getCollectionFetchPromise();
            // Only update categories and widgets for the active tab (or all if no tabs)
            var d2 = this.dashboardCategoryCollection ? this.dashboardCategoryCollection.getCollectionFetchPromise() : $.Deferred().resolve();
            var d3 = this.dashboardWidgetCollection ? this.dashboardWidgetCollection.getCollectionFetchPromise() : $.Deferred().resolve();

            $.when(d1, d2, d3).done(() => {
                if (this.$('.body .categories').is(":visible")) this.handlePotentialConfigurationHashChange();
                this.dashboardUpdateTimer = setTimeout(_.bind(this.perpetuallyUpdate, this), DASHBOARD_UPDATE_INTERVAL);
            }).fail(() => {
                // Back off a little bit if something goes wrong as to not additionally stress the server
                this.dashboardUpdateTimer = setTimeout(_.bind(this.perpetuallyUpdate, this), DASHBOARD_FAILURE_UPDATE_INTERVAL);
            })
        }
    }

    private perpetuallyUpdateChart() {
        clearTimeout(this.chartUpdateTimer);

        if (!this.$('.body .categories').is(":visible")) {
            this.chartUpdateTimer = setTimeout(_.bind(this.perpetuallyUpdateChart, this), CHART_UPDATE_MIN_INTERVAL);
            return;
        }
        if (!this.graphedChannelsCollection || !this.graphedChannelsCollection.channelIds || !this.graphedChannelsCollection.channelIds.length) {
            return;
        }

        $.when(this.graphedChannelsCollection.getCollectionFetchPromise()).done(() => {
            this.syncGraphedChannelsToAllChannels();
            this.scheduleChartUpdate();
        }).fail(() => {
            this.chartUpdateTimer = setTimeout(_.bind(this.perpetuallyUpdateChart, this), DASHBOARD_FAILURE_UPDATE_INTERVAL);
        });
    }

    private getChartUpdateInterval(): number {
        if (!this.graphedChannelsCollection || !this.graphedChannelsCollection.length) {
            return DASHBOARD_UPDATE_INTERVAL;
        }

        let minFrequency = Infinity;
        this.graphedChannelsCollection.each((channel) => {
            const freq = channel.get('module_polling_frequency');
            if (freq && freq > 0) {
                minFrequency = Math.min(minFrequency, freq);
            }
        });

        if (minFrequency === Infinity) return DASHBOARD_UPDATE_INTERVAL;
        return Math.min(Math.max(minFrequency * 1000, CHART_UPDATE_MIN_INTERVAL), DASHBOARD_UPDATE_INTERVAL);
    }

    private scheduleChartUpdate() {
        clearTimeout(this.chartUpdateTimer);
        const interval = this.getChartUpdateInterval();
        this.chartUpdateTimer = setTimeout(_.bind(this.perpetuallyUpdateChart, this), interval);
    }

    private updateGraphedChannelIds() {
        if (!this.graphedChannelsCollection) return;

        let channelIds: number[] = [];
        App.Bus.trigger('dashboard:category:all:chart:getChannels', (ids: number[]) => {
            channelIds = ids;
        });

        const prev = (this.graphedChannelsCollection.channelIds || []).slice().sort();
        const sorted = channelIds.slice().sort();
        const changed = sorted.length !== prev.length || !sorted.every((id, i) => id === prev[i]);
        this.graphedChannelsCollection.channelIds = channelIds.slice();

        if (!changed) return;

        // Fetch immediately when channels change
        this.perpetuallyUpdateChart();
    }

    private syncGraphedChannelsToAllChannels() {
        if (!this.graphedChannelsCollection || !this.allChannelsCollection) return;
        if (!this.graphedChannelsCollection.length) return;

        this.graphedChannelsCollection.each((graphedChannel) => {
            const channel = this.allChannelsCollection.get(graphedChannel.id);
            if (channel) {
                channel.set(graphedChannel.attributes);
            }
        });
        this.allChannelsCollection.trigger('sync');
    }

    private isEmpty() {
        return this.collectionLoaded && this.dashboardWidgetCollection.filter((widget) => {
            return widget.get("dashboard_widget_type_id") !== 4
        }).length < 1
    }

    render() {
        var db = this.options.dashboardCollection.findWhere({ id: this.dashboardId });
        this.$el.html(this.template({
            permissionCollection: this.options.permissionCollection,
            dashboardId: this.dashboardId,
            collectionLoaded: this.collectionLoaded,
            dashboardName: db ? db.get('name') : '',
            currentUser: this.options.currentUser,
            banner: require('../../../img/apps/dashboard/dashboard-banner.svg'),
            // Indicate whether the dashboard contains any widget besides the default
            dashboardEmpty: this.dashboardEmpty,
        }));

        this.renderSubViews();
        return this;
    }
    
    private showLoadingSpinner() {
        // Show spinner in categories container without re-rendering entire dashboard
        var categoriesContainer = this.$('.body .categories');
        if (categoriesContainer.length === 0) {
            var wasLoaded = this.collectionLoaded;
            this.collectionLoaded = true;
            this.render();
            this.collectionLoaded = wasLoaded;
            return;
        }
        // Ensure categories area is visible and empty state hidden during loading
        this.$('.body .dashboard-empty-state').addClass('tw-hidden');
        categoriesContainer.removeClass('tw-hidden');
        categoriesContainer.html('<div class="spinner-table"><div class="spinner-wrapper"><div class="spinner-loader">Loading…</div></div></div>');
    }
    
    private renderContentOnly() {
        // Only update the content area, not the entire dashboard template
        // This preserves the header/title section
        if (!this.collectionLoaded) {
            // Show spinner in categories container
            var categoriesContainer = this.$('.body .categories');
            if (categoriesContainer.length > 0) {
                categoriesContainer.html('<div class="spinner-table"><div class="spinner-wrapper"><div class="spinner-loader">Loading…</div></div></div>');
            }
            return;
        }
        
        // Update visibility of empty state and content sections
        var emptySection = this.$('.body .dashboard-empty-state');
        var categoriesContainer = this.$('.body .categories');
        var graphCategoryContainer = this.$('.body .graph-category');
        
        if (this.dashboardEmpty) {
            // Show empty dashboard message, hide content
            emptySection.removeClass('tw-hidden');
            categoriesContainer.addClass('tw-hidden');
            graphCategoryContainer.addClass('tw-hidden');
        } else {
            // Hide empty section and show categories
            emptySection.addClass('tw-hidden');
            categoriesContainer.removeClass('tw-hidden');
            
            // Render subviews (tabs and categories) - this will handle Graph category
            this.renderSubViews();
            
            // Graph category container should always be visible if Graph category exists
            if (this.graphCategoryView && this.graphCategoryView.$el.parent().length > 0) {
                graphCategoryContainer.removeClass('tw-hidden');
            }
        }
    }

    private renderSubViews() {
        // Don't render subviews if data isn't loaded yet
        if (!this.collectionLoaded) return;

        // Render tabs - skip recreation when tabs already in DOM (tab switch) to preserve scroll
        if (this.dashboardTabCollection && this.dashboardTabCollection.length > 0) {
            let tabsContainer = this.$('.dashboard-tabs-container');
            const tabsAlreadyInDom = this.tabsView && this.tabsView.$el && tabsContainer.length > 0 &&
                tabsContainer.find('.dashboard-tabs').length > 0;

            if (!tabsAlreadyInDom) {
                if (this.tabsView) {
                    this.tabsView.remove();
                }
                if (tabsContainer.length === 0) {
                    const headerRow = this.$('.dashboard__toolbar').parent();
                    if (headerRow.length > 0) {
                        headerRow.prepend('<div class="dashboard-tabs-container tw-flex-1"></div>');
                        tabsContainer = this.$('.dashboard-tabs-container');
                    }
                }

                this.tabsView = new DashboardTabsView({
                    dashboardTabCollection: this.dashboardTabCollection,
                    activeTabId: this.activeTabId,
                    permissionCollection: this.options.permissionCollection,
                    dashboardId: this.dashboardId,
                    onTabSwitch: (tabId: number) => {
                        this.handleTabSwitch(tabId);
                    },
                    onTabAdd: () => {
                        this.handleTabAdd();
                    },
                    onTabEdit: (tab) => {
                        this.handleTabEdit(tab);
                    },
                    onTabClone: (tab) => {
                        this.handleTabClone(tab);
                    },
                    onTabMove: (tab) => {
                        this.handleTabMove(tab);
                    },
                    onTabDelete: (tab) => {
                        this.handleTabDelete(tab);
                    },
                    onTabReorder: (sourceTabId: number, targetTabId: number) => {
                        this.handleTabReorder(sourceTabId, targetTabId);
                    }
                });

                if (tabsContainer.length > 0) {
                    tabsContainer.find('.dashboard-tabs-skeleton').addClass('tw-hidden');
                    tabsContainer.empty().append(this.tabsView.render().$el);
                    tabsContainer.show();
                }
            } else {
                this.tabsView.options.activeTabId = this.activeTabId;
                this.tabsView.setActiveTab(this.activeTabId);
            }
        } else {
            // Hide tabs container if no tabs
            this.$('.dashboard-tabs-container').hide();
        }

        var categoriesContainer = this.$('.body .categories');
        var graphCategoryContainer = this.$('.body .graph-category');
        // Separate Graph category (persistent) from tab-specific categories
        var graphCategory = this.dashboardCategoryCollection.find((category) => {
            return category.get('minimal') === 1 || category.get('dashboard_tab_id') === null;
        });
        var tabSpecificCategories = this.dashboardCategoryCollection.filter((category) => {
            return category.get('minimal') !== 1 && category.get('dashboard_tab_id') !== null;
        });
        // Remove only tab-specific category views (Graph category stays in separate container)
        _.each(this.categoryViews, function (view) {
            view.remove();
        });
        this.categoryViews = [];
        
        // Clear only tab-specific categories container (Graph category has its own container)
        categoriesContainer.empty();
        
        // Render tab-specific categories
        _.each(tabSpecificCategories, (category) => {
            var view = new DashboardCategoryView({
                model: category,
                permissionCollection: this.options.permissionCollection,
                dashboardChannelCollection: this.dashboardChannelCollection,
                dashboardCategoryCollection: this.dashboardCategoryCollection,
                dashboardWidgetCollection: this.dashboardWidgetCollection,
                widgetManager: this.widgetManager,
                dashboardId: this.dashboardId,
                dashboardTabCollection: this.dashboardTabCollection
            });
            this.categoryViews.push(view);
            categoriesContainer.append(view.$el);
            view.render();
            view.setLayout();
        });
        
        // Handle Graph category (persistent - only create if doesn't exist, in separate container)
        // Graph category needs access to ALL channels (not filtered by tab), so use allChannelsCollection
        if (graphCategory) {
            // Ensure allChannelsCollection is loaded for Graph category
            if (!this.allChannelsCollection) {
                this.allChannelsCollection = new DashboardChannelCollection([], { 
                    dashboardId: this.dashboardId
                });
            }
            
            if (!this.graphCategoryView) {
                // Create Graph category view only once - use allChannelsCollection for access to all channels
                this.graphCategoryView = new DashboardCategoryView({
                    model: graphCategory,
                    permissionCollection: this.options.permissionCollection,
                    dashboardChannelCollection: this.allChannelsCollection, // Use unfiltered collection
                    dashboardCategoryCollection: this.dashboardCategoryCollection,
                    dashboardWidgetCollection: this.dashboardWidgetCollection,
                    widgetManager: this.graphWidgetManager, // Use separate widget manager with allChannelsCollection
                    dashboardId: this.dashboardId,
                    dashboardTabCollection: this.dashboardTabCollection
                });
                // Append Graph category to its own container
                if (graphCategoryContainer.length > 0) {
                    graphCategoryContainer.append(this.graphCategoryView.$el);
                    this.graphCategoryView.render();
                    this.graphCategoryView.setLayout();
                    graphCategoryContainer.removeClass('tw-hidden');
                }
            } else {
                // Update the model reference and collection reference in case it changed
                this.graphCategoryView.model = graphCategory;
                // Update the channel collection to use allChannelsCollection (unfiltered)
                this.graphCategoryView.options.dashboardChannelCollection = this.allChannelsCollection;
                // Update widget manager reference to use graphWidgetManager
                this.graphCategoryView.options.widgetManager = this.graphWidgetManager;
                // Update widget manager's collection reference
                if (this.graphWidgetManager) {
                    this.graphWidgetManager.options.dashboardChannelCollection = this.allChannelsCollection;
                }
                // Update all widget views' collection references in the Graph category
                if (this.graphCategoryView.widgetViews) {
                    _.each(this.graphCategoryView.widgetViews, (widgetView) => {
                        if (widgetView.options) {
                            widgetView.options.dashboardChannelCollection = this.allChannelsCollection;
                        }
                    });
                }
                // Ensure Graph category is in its container and visible
                if (this.graphCategoryView.$el.parent().length === 0 && graphCategoryContainer.length > 0) {
                    graphCategoryContainer.append(this.graphCategoryView.$el);
                }
                // Ensure container is visible
                if (graphCategoryContainer.length > 0) {
                    graphCategoryContainer.removeClass('tw-hidden');
                }
            }
        }
    }

    private handleEditDashboardClick() {
        var view = new ModifyDashboardModal({
            appRegistry: this.options.appRegistry,
            appCollection: this.options.appCollection,
            brandCollection: this.options.brandCollection,
            installCollection: this.options.installCollection,
            permissionCollection: this.options.permissionCollection,
            organizationCollection: this.options.organizationCollection,
            dashboardCollection: this.options.dashboardCollection,
            dashboardChannelCollection: this.options.dashboardChannelCollection,
            dashboardId: this.dashboardId,
            currentUser: this.options.currentUser,
            modelId: this.dashboardId
        });
        $('.modals').append(view.render().el);
        view.show();
    }

    private handleEditDashboardNotificationsClick() {
        var view = new ModifyDashboardNotificationsModal({
            appRegistry: this.options.appRegistry,
            appCollection: this.options.appCollection,
            brandCollection: this.options.brandCollection,
            installCollection: this.options.installCollection,
            permissionCollection: this.options.permissionCollection,
            organizationCollection: this.options.organizationCollection,
            dashboardCollection: this.options.dashboardCollection,
            dashboardChannelCollection: this.dashboardChannelCollection,
            dashboardCategoryCollection: this.dashboardCategoryCollection,
            dashboardId: this.dashboardId,
            currentUser: this.options.currentUser,
            modelId: this.dashboardId
        });
        $('.modals').append(view.render().el);
        view.show();
    }

    private handleCloneDashboardClick() {
        var db = this.options.dashboardCollection.findWhere({ id: this.dashboardId });

        var view = new ModifyDashboardModal({
            appRegistry: this.options.appRegistry,
            appCollection: this.options.appCollection,
            brandCollection: this.options.brandCollection,
            installCollection: this.options.installCollection,
            permissionCollection: this.options.permissionCollection,
            organizationCollection: this.options.organizationCollection,
            dashboardCollection: this.options.dashboardCollection,
            dashboardChannelCollection: this.options.dashboardChannelCollection,
            dashboardId: this.dashboardId,
            currentUser: this.options.currentUser,
            name: db.get('name'),
            clone: true,
            modelId: this.dashboardId
        });
        $('.modals').append(view.render().el);
        view.show();
    }

    private handleBulkGenerateClick() {
        var view = new BulkGenerateModalView({
            appRegistry: this.options.appRegistry,
            appCollection: this.options.appCollection,
            brandCollection: this.options.brandCollection,
            installCollection: this.options.installCollection,
            permissionCollection: this.options.permissionCollection,
            organizationCollection: this.options.organizationCollection,
            dashboardCollection: this.options.dashboardCollection,
            dashboardChannelCollection: this.dashboardChannelCollection,
            dashboardCategoryCollection: this.dashboardCategoryCollection,
            dashboardId: this.dashboardId,
            dashboardWidgetCollection: this.dashboardWidgetCollection,
            currentUser: this.options.currentUser,
            modelId: null
        });
        $('.modals').append(view.render().$el);
        view.show();
    }

    private handleAddWidgetClick() {
        var centerRatio = 1 / 2;
        var documentViewportCenter = (<any>$)(window).scrollTop() + (<any>$)(window).height() * centerRatio;
        var focusedCategory = _.minBy(this.$(".category:not(.minimal)"), (category) => {
            //Min of distance from center and bottom or top of category
            return Math.min(Math.abs($(category).offset().top - documentViewportCenter),
                Math.abs($(category).offset().top + $(category).height() - documentViewportCenter));
        });
        var view = new ModifyDashboardWidgetModal({
            appRegistry: this.options.appRegistry,
            appCollection: this.options.appCollection,
            brandCollection: this.options.brandCollection,
            installCollection: this.options.installCollection,
            permissionCollection: this.options.permissionCollection,
            organizationCollection: this.options.organizationCollection,
            dashboardCollection: this.options.dashboardCollection,
            dashboardChannelCollection: this.dashboardChannelCollection,
            dashboardCategoryCollection: this.dashboardCategoryCollection,
            dashboardTabCollection: this.dashboardTabCollection,
            dashboardWidgetCollection: this.dashboardWidgetCollection,
            dashboardId: this.dashboardId,
            moduleCollections: this.moduleCollections,
            currentUser: this.options.currentUser,
            widget: null,
            categoryId: Number($(focusedCategory).data('category-id')),
            modelId: null,
            validWidgets: this.validWidgets,
            activeTabId: this.activeTabId
        });
        $('.modals').append(view.render().$el);
        view.show();
    }

    private handleTabAdd() {
        const maxOrder = this.dashboardTabCollection.length > 0 
            ? Math.max(...this.dashboardTabCollection.map(tab => tab.get('order'))) 
            : 0;
        const newOrder = maxOrder + 1;

        const view = new ModifyDashboardTabModal({
            dashboardId: this.dashboardId,
            name: '',
            order: newOrder,
            modelId: null,
            onSave: (name: string, order?: number) => {
                return $.ajax({
                    type: "POST",
                    url: `/api/v1/dashboards/${this.dashboardId}/tabs`,
                    data: JSON.stringify({
                        name: name,
                        order: order || newOrder
                    }),
                    dataType: 'json',
                    contentType: "application/json; charset=utf-8"
                }).done((response) => {
                    // Show skeleton while fetching
                    if (this.tabsView) {
                        this.tabsView.showSkeleton();
                    }
                    
                    // Switch to the new tab immediately, then refresh collection
                    if (response.tabId) {
                        this.activeTabId = response.tabId;
                        this.updateUrlWithTab(response.tabId);
                        
                        // Refresh the tabs collection
                        this.dashboardTabCollection.fetch({
                            success: (collection, resp, options) => {
                                // Hide skeleton
                                if (this.tabsView) {
                                    this.tabsView.hideSkeleton();
                                }
                                // Update tabs view to reflect the new active tab
                                if (this.tabsView) {
                                    this.tabsView.setActiveTab(response.tabId);
                                }
                                // Load the tab data
                                this.loadTabData();
                            },
                            error: (collection, resp, options) => {
                                // Hide skeleton even on error
                                if (this.tabsView) {
                                    this.tabsView.hideSkeleton();
                                }
                                // Still try to switch even if fetch fails
                                if (this.tabsView) {
                                    this.tabsView.setActiveTab(response.tabId);
                                }
                                this.loadTabData();
                            }
                        });
                    } else {
                        App.Bus.trigger('dashboard:config:changed');
                    }
                }).fail((error) => {
                    // Error creating tab
                });
            }
        });
        $('.modals').append(view.render().el);
        view.show();
    }

    private handleTabClone(tab: DashboardTab) {
        const maxOrder = this.dashboardTabCollection.length > 0 
            ? Math.max(...this.dashboardTabCollection.map(t => t.get('order'))) 
            : 0;
        const newOrder = maxOrder + 1;
        const clonedName = tab.get('name') + ' (Clone)';

        $.ajax({
            type: "POST",
            url: `/api/v1/dashboards/${this.dashboardId}/tabs/${tab.get('id')}/clone`,
            data: JSON.stringify({
                name: clonedName,
                order: newOrder
            }),
            dataType: 'json',
            contentType: "application/json; charset=utf-8"
        }).done((response) => {
            // Show skeleton while fetching
            if (this.tabsView) {
                this.tabsView.showSkeleton();
            }
            
            // Switch to the cloned tab immediately, then refresh collection
            if (response.tabId) {
                this.activeTabId = response.tabId;
                this.updateUrlWithTab(response.tabId);
                
                // Refresh the tabs collection
                this.dashboardTabCollection.fetch({
                    success: (collection, resp, options) => {
                        // Hide skeleton
                        if (this.tabsView) {
                            this.tabsView.hideSkeleton();
                        }
                        // Update tabs view to reflect the new active tab
                        if (this.tabsView) {
                            this.tabsView.setActiveTab(response.tabId);
                        }
                        // Load the tab data
                        this.loadTabData();
                    },
                    error: (collection, resp, options) => {
                        // Hide skeleton even on error
                        if (this.tabsView) {
                            this.tabsView.hideSkeleton();
                        }
                        // Still try to switch even if fetch fails
                        if (this.tabsView) {
                            this.tabsView.setActiveTab(response.tabId);
                        }
                        this.loadTabData();
                    }
                });
            } else {
                App.Bus.trigger('dashboard:config:changed');
            }
        }).fail((error) => {
            App.Bus.trigger('dashboard:config:changed');
        });
    }

    private handleTabEdit(tab: DashboardTab) {
        const view = new ModifyDashboardTabModal({
            dashboardId: this.dashboardId,
            tabId: tab.get('id'),
            name: tab.get('name'),
            order: tab.get('order'),
            modelId: tab.get('id'),
            onSave: (name: string, order?: number) => {
                return $.ajax({
                    type: "PUT",
                    url: `/api/v1/dashboards/${this.dashboardId}/tabs/${tab.get('id')}`,
                    data: JSON.stringify({
                        name: name,
                        order: order || tab.get('order')
                    }),
                    dataType: 'json',
                    contentType: "application/json; charset=utf-8"
                }).done(() => {
                    // Refresh the tabs collection
                    this.dashboardTabCollection.fetch({
                        success: () => {
                            App.Bus.trigger('dashboard:config:changed');
                        }
                    });
                });
            }
        });
        $('.modals').append(view.render().el);
        view.show();
    }

    private handleTabMove(tab: DashboardTab) {
        const self = this;
        
        // Fetch all dashboards and filter by modify permissions
        $.when(this.options.dashboardCollection.getCollectionFetchPromise()).done(() => {
            // Filter dashboards where user has modify permissions, excluding current dashboard
            const moveableDashboards = this.options.dashboardCollection.filter((dashboard) => {
                const dashboardId = dashboard.get('id');
                return dashboardId !== this.dashboardId && 
                       this.options.permissionCollection.canModifyDashboard(dashboardId);
            });

            if (moveableDashboards.length === 0) {
                (<any>$).notify({
                    title: 'No Dashboards Available',
                    message: 'You need modify permissions on at least one other dashboard to clone tabs to.',
                    icon: 'fa fa-exclamation-circle',
                }, {
                    animate: {
                        enter: 'animated fadeInUp',
                    },
                    type: "warning-alt",
                    placement: { from: "bottom", align: "center" }
                });
                return;
            }

            // Create a filtered collection for the modal
            const filteredDashboardCollection = new DashboardCollection(moveableDashboards);

            const cloneTabNoteHtml = [
                "Your tab will be copied to the selected dashboard with all its categories, widgets, and thresholds. ",
                "You'll need to set up dashboard alerts separately on the target dashboard."
            ].join('');

            const modal = new GenericDashboardSelectModal({
                titleText: 'Clone Tab to Dashboard',
                text: 'Choose a target dashboard to clone this tab to (the original tab will remain unchanged):',
                buttonText: 'Clone',
                noteHtml: cloneTabNoteHtml,
                dashboardCollection: filteredDashboardCollection,
                currentDashboardId: this.dashboardId,
                func: (targetDashboardId: number) => {
                    return $.ajax({
                        type: "POST",
                        url: `/api/v1/dashboards/${this.dashboardId}/tabs/${tab.get('id')}/move`,
                        data: JSON.stringify({
                            targetDashboardId: targetDashboardId
                        }),
                        dataType: 'json',
                        contentType: "application/json; charset=utf-8"
                    }).done((response: { tabId: number; targetDashboardId: number }) => {
                        // Open target dashboard in a new tab with the cloned tab selected, and focus the window
                        const clonedTabId = response.tabId;
                        const url = window.location.origin + window.location.pathname + '#/dashboard/' + targetDashboardId + '?tab=' + clonedTabId;
                        const newWindow = window.open(url, '_blank');
                        if (newWindow) {
                            newWindow.focus();
                        }
                    }).fail((err) => {
                        // Handle AJAX error
                        var errorMessage = 'Error cloning tab to dashboard';
                        if (err && err.responseJSON && err.responseJSON.message) {
                            errorMessage = err.responseJSON.message;
                        }
                        (<any>$).notify({
                            title: 'Error',
                            message: errorMessage,
                            icon: 'fa fa-exclamation-circle',
                        }, {
                            animate: {
                                enter: 'animated fadeInUp',
                            },
                            type: "danger-alt",
                            placement: { from: "bottom", align: "center" }
                        });
                    });
                }
            });
            $('.modals').append(modal.render().el);
            modal.show();
        });
    }

    private handleTabDelete(tab: DashboardTab) {
        const modal = new GenericDeleteModal({
            type: 'Dashboard Tab',
            name: tab.get('name'),
            func: () => {
                const wasActiveTab = this.activeTabId === tab.get('id');
                const self = this;
                
                return $.ajax({
                    type: "DELETE",
                    url: `/api/v1/dashboards/${this.dashboardId}/tabs/${tab.get('id')}`,
                    dataType: 'json',
                    contentType: "application/json; charset=utf-8"
                }).done(() => {
                    // Remove the tab from collection immediately
                    const deletedTab = self.dashboardTabCollection.findWhere({ id: tab.get('id') });
                    if (deletedTab) {
                        self.dashboardTabCollection.remove(deletedTab);
                    }
                    
                    // Switch to first tab immediately, then refresh collection
                    if (self.dashboardTabCollection.length > 0) {
                        const firstTab = self.dashboardTabCollection.at(0);
                        const firstTabId = firstTab.get('id');
                        
                        // Set activeTabId and update URL immediately
                        self.activeTabId = firstTabId;
                        self.updateUrlWithTab(firstTabId);
                    } else {
                        self.activeTabId = null;
                        self.updateUrlWithTab(null);
                    }
                    
                    // Refresh the tabs collection
                    self.dashboardTabCollection.fetch({
                        success: (collection, resp, options) => {
                            // Hide skeleton
                            if (self.tabsView) {
                                self.tabsView.hideSkeleton();
                            }
                            // Update tabs view to reflect the new active tab
                            if (self.tabsView && self.activeTabId) {
                                self.tabsView.setActiveTab(self.activeTabId);
                            } else if (!self.activeTabId) {
                                if (self.tabsView) {
                                    self.tabsView.setActiveTab(null);
                                }
                            }
                            
                            // Load the tab data
                            self.loadTabData();
                        },
                        error: (collection, resp, options) => {
                            // Hide skeleton even on error
                            if (self.tabsView) {
                                self.tabsView.hideSkeleton();
                            }
                            // Still update tabs view and load data even on error
                            if (self.tabsView && self.activeTabId) {
                                self.tabsView.setActiveTab(self.activeTabId);
                            }
                            self.loadTabData();
                        }
                    });
                }).fail((error) => {
                    // On AJAX failure, reload to clear loading state
                    self.loadTabData();
                });
            }
        });
        $('.modals').append(modal.render().el);
        modal.show();
    }

    private handleTabReorder(sourceTabId: number, targetTabId: number) {
        const self = this;
        
        // Get the source and target tabs from the collection
        const sourceTab = this.dashboardTabCollection.findWhere({ id: sourceTabId });
        const targetTab = this.dashboardTabCollection.findWhere({ id: targetTabId });
        
        if (!sourceTab || !targetTab) {
            console.error('Source or target tab not found', { sourceTabId, targetTabId, sourceTab, targetTab });
            return;
        }
        
        const sourceOrder = sourceTab.get('order');
        const targetOrder = targetTab.get('order');
        
        // If orders are the same, no need to reorder
        if (sourceOrder === targetOrder) {
            return;
        }
        
        // Get all tabs sorted by order
        const allTabs = this.dashboardTabCollection.models.slice().sort((a, b) => {
            return a.get('order') - b.get('order');
        });
        
        // Calculate new orders for all affected tabs
        const updates: Array<{ tab: DashboardTab, oldOrder: number, newOrder: number }> = [];
        
        if (sourceOrder < targetOrder) {
            // Moving right: shift tabs between source and target left by 1
            allTabs.forEach((tab) => {
                const tabOrder = tab.get('order');
                const tabId = tab.get('id');
                
                if (tabId === sourceTabId) {
                    updates.push({ tab, oldOrder: tabOrder, newOrder: targetOrder });
                } else if (tabOrder > sourceOrder && tabOrder <= targetOrder) {
                    updates.push({ tab, oldOrder: tabOrder, newOrder: tabOrder - 1 });
                }
            });
        } else {
            // Moving left: shift tabs between target and source right by 1
            allTabs.forEach((tab) => {
                const tabOrder = tab.get('order');
                const tabId = tab.get('id');
                
                if (tabId === sourceTabId) {
                    updates.push({ tab, oldOrder: tabOrder, newOrder: targetOrder });
                } else if (tabOrder >= targetOrder && tabOrder < sourceOrder) {
                    updates.push({ tab, oldOrder: tabOrder, newOrder: tabOrder + 1 });
                }
            });
        }
        
        // OPTIMISTIC UPDATE: Immediately update the UI
        updates.forEach((update) => {
            update.tab.set('order', update.newOrder, { silent: true });
        });
        
        // Sort and trigger re-render
        this.dashboardTabCollection.sort();
        if (this.tabsView) {
            this.tabsView.render();
        }
        
        // Now sync with server in the background
        // Use a temporary high order value to avoid conflicts during reordering
        const maxOrder = Math.max(...allTabs.map(t => t.get('order')));
        const tempOrder = maxOrder + 1000;
        
        // Build the update chain
        let updateChain = Promise.resolve();
        const sourceUpdate = updates.find(u => u.tab.get('id') === sourceTabId);
        const shiftUpdates = updates.filter(u => u.tab.get('id') !== sourceTabId);
        
        // Step 1: Move source tab to temporary order
        if (sourceUpdate) {
            updateChain = updateChain.then(() => {
                return $.ajax({
                    type: "PUT",
                    url: `/api/v1/dashboards/${this.dashboardId}/tabs/${sourceTabId}`,
                    data: JSON.stringify({
                        name: sourceUpdate.tab.get('name'),
                        order: tempOrder
                    }),
                    dataType: 'json',
                    contentType: "application/json; charset=utf-8"
                });
            });
        }
        
        // Step 2: Update tabs that need to shift
        shiftUpdates.forEach((update) => {
            updateChain = updateChain.then(() => {
                return $.ajax({
                    type: "PUT",
                    url: `/api/v1/dashboards/${this.dashboardId}/tabs/${update.tab.get('id')}`,
                    data: JSON.stringify({
                        name: update.tab.get('name'),
                        order: update.newOrder
                    }),
                    dataType: 'json',
                    contentType: "application/json; charset=utf-8"
                });
            });
        });
        
        // Step 3: Move source tab to its final position
        if (sourceUpdate) {
            updateChain = updateChain.then(() => {
                return $.ajax({
                    type: "PUT",
                    url: `/api/v1/dashboards/${this.dashboardId}/tabs/${sourceTabId}`,
                    data: JSON.stringify({
                        name: sourceUpdate.tab.get('name'),
                        order: sourceUpdate.newOrder
                    }),
                    dataType: 'json',
                    contentType: "application/json; charset=utf-8"
                });
            });
        }
        
        // Handle completion/errors in background
        updateChain.then(() => {
            App.Bus.trigger('dashboard:config:changed');
        }).catch((error) => {
            console.error('Failed to sync tab order with server:', error);
            // Revert optimistic update on error
            updates.forEach((update) => {
                update.tab.set('order', update.oldOrder, { silent: true });
            });
            this.dashboardTabCollection.sort();
            if (this.tabsView) {
                this.tabsView.render();
            }
            // Show error to user (optional - you could add a toast notification here)
        });
    }

    remove() {
        if (this.tabsView) {
            this.tabsView.remove();
        }
        if (this.graphCategoryView) {
            this.graphCategoryView.remove();
        }
        _.each(this.categoryViews, (v) => {
            v.remove()
        });
        clearTimeout(this.dashboardUpdateTimer);
        clearTimeout(this.chartUpdateTimer);
        return super.remove();
    }
}


// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/DashboardView.ts